import i18n from '@/utils/i18n';
import React, { ReactElement } from 'react';
import { Button, Icons, Tooltip, TooltipGroup } from '@usepepper/sprout-kit-ui';
import { createStyles } from '@/contexts/theme-context';
import { LoadQuoteDropdown } from '@/experiences/sales-portal/domain/pricing/screens/quotes/components/load-quote-dropdown';
import { Platform, View } from 'react-native';
import { PriceQuoteSummary } from '@/experiences/sales-portal/domain/pricing/screens/quotes/blocs/loaders/use-saved-quotes-loader';
import { Typography } from '@usepepper/ui/atoms/typography';

const IconButtonGroup: React.FC<{ children: React.ReactNode }> = ({
  children,
}) =>
  Platform.OS === 'web' ? (
    <TooltipGroup delay={{ close: 100, open: 300 }}>{children}</TooltipGroup>
  ) : (
    <>{children}</>
  );

interface IconButtonWithTooltipProps {
  accessibilityLabel: string;
  busy?: boolean;
  disabled: boolean;
  icon: ReactElement;
  onPress: () => void;
  tooltipLabel: string;
}

const IconButtonWithTooltip: React.FC<IconButtonWithTooltipProps> = ({
  accessibilityLabel,
  busy,
  disabled,
  icon,
  onPress,
  tooltipLabel,
}) => {
  const [, theme] = useStyles();
  const button = (
    <Button
      accessibilityLabel={accessibilityLabel}
      accessibilityState={{
        busy: busy ?? false,
        disabled,
      }}
      disabled={disabled}
      icon={icon}
      onPress={onPress}
      size="$sm"
      variant="secondary"
    />
  );
  if (Platform.OS !== 'web') {
    return button;
  }
  return (
    <Tooltip placement="bottom">
      {/*
       * Wrap the button so hover events anchor to a non-disabled element —
       * Tamagui suppresses pointer events on disabled triggers and the
       * tooltip would otherwise never fire while the action is unavailable.
       */}
      <Tooltip.Trigger>
        <View>{button}</View>
      </Tooltip.Trigger>
      <Tooltip.Content>
        <Typography
          color={theme.sk.color.text.ondark}
          variant="paragraph-sm-600"
        >
          {tooltipLabel}
        </Typography>
      </Tooltip.Content>
    </Tooltip>
  );
};

interface QuoteActionButtonsProps {
  accountSelected: boolean;
  canSave: boolean;
  isExportingCsv: boolean;
  isExportingPdf: boolean;
  onDuplicate: () => void;
  onExportCsv: () => void;
  onExportPdf: () => void;
  onNewQuote: () => void;
  onSave: () => void;
  onSelectQuote: (quote: PriceQuoteSummary) => void;
  quoteId: string | null;
  savedQuotes: PriceQuoteSummary[];
  saving: boolean;
}

/**
 * Top-bar-right cluster for the quote editor: PDF / CSV / Duplicate exports,
 * the Load Quote dropdown, and Save.
 *
 * Export requires both an existing quote and a selected account — the
 * mutation needs a `chat_id` from the account for the backend's permission
 * check. Duplicate requires an existing quote. Save is enabled when the
 * caller-computed `canSave` is true and the mutation isn't already in flight.
 * The Load Quote dropdown lists prior quotes (pulled in by the parent screen)
 * and exposes "+ New quote".
 */
export const QuoteActionButtons: React.FC<QuoteActionButtonsProps> = ({
  accountSelected,
  canSave,
  isExportingCsv,
  isExportingPdf,
  onDuplicate,
  onExportCsv,
  onExportPdf,
  onNewQuote,
  onSave,
  onSelectQuote,
  quoteId,
  savedQuotes,
  saving,
}) => {
  const [styles] = useStyles();
  const isExistingQuote = quoteId !== null;
  const canExport = isExistingQuote && accountSelected;

  return (
    <View style={styles.container}>
      <IconButtonGroup>
        <IconButtonWithTooltip
          accessibilityLabel={i18n.t('sales_portal.price_quotes.export_pdf')}
          busy={isExportingPdf}
          disabled={!canExport || isExportingPdf}
          icon={<Icons.FileText size={16} />}
          onPress={onExportPdf}
          tooltipLabel={i18n.t('sales_portal.price_quotes.pdf')}
        />
        <IconButtonWithTooltip
          accessibilityLabel={i18n.t('sales_portal.price_quotes.export_csv')}
          busy={isExportingCsv}
          disabled={!canExport || isExportingCsv}
          icon={<Icons.FileSpreadsheet size={16} />}
          onPress={onExportCsv}
          tooltipLabel={i18n.t('sales_portal.price_quotes.csv')}
        />
        <IconButtonWithTooltip
          accessibilityLabel={i18n.t('sales_portal.price_quotes.duplicate')}
          disabled={!isExistingQuote}
          icon={<Icons.Copy size={16} />}
          onPress={onDuplicate}
          tooltipLabel={i18n.t('sales_portal.price_quotes.duplicate')}
        />
      </IconButtonGroup>
      <LoadQuoteDropdown
        onNewQuote={onNewQuote}
        onSelectQuote={onSelectQuote}
        savedQuotes={savedQuotes}
      />
      <Button
        accessibilityState={{ busy: saving, disabled: !canSave || saving }}
        disabled={!canSave || saving}
        onPress={onSave}
        size="$sm"
        variant="primary"
      >
        {i18n.t('sales_portal.price_quotes.save')}
      </Button>
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  container: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSmall,
  },
}));
